import { HTTPError } from 'ky';

export const MAX_ALIAS_NAME_LENGTH = 255;

/**
 * The duplicate message, worded for the kind of record (the server's own: AssetAlias's taken_on_owner).
 * @param {string} recordLabel - What the aliases are on: "tract" or "development area".
 * @return {string} The message.
 */
export const duplicateAliasMessage = (recordLabel: string): string =>
  `This ${recordLabel} already has that alias`;
export const COMMA_ALIAS_MESSAGE = "Aliases can't contain commas";
// Worded as the server's own message for the same rule (Rails' too_long on name), so the form's pre-flight
// check and the 422 say one thing. asset-alias-name-rules.json is the contract both sides are tested with.
export const TOO_LONG_ALIAS_MESSAGE = `Name is too long (maximum is ${MAX_ALIAS_NAME_LENGTH} characters)`;

// The server trims and squeezes ASCII whitespace only (Ruby's strip and /\s+/), so a no-break space is a
// different character there. JS's own trim and \s would also take it, and the form would then call
// "Gulf 7" and the same name with a no-break space a duplicate when the server accepts both.
const ASCII_WHITESPACE_RUN = /[ \t\r\n\f\v]+/g;
const ASCII_WHITESPACE_ENDS = /^[ \t\r\n\f\v]+|[ \t\r\n\f\v]+$/g;

/**
 * The name as the server stores it: trimmed, as typed.
 * @param {string} name - What the user typed.
 * @return {string} The trimmed name.
 */
export const trimAliasName = (name: string): string =>
  name.replace(ASCII_WHITESPACE_ENDS, '');

/**
 * The form the server compares names in (AssetAlias.normalize): trimmed, lowercased, inner whitespace
 * squeezed to one space. Two names with the same normal form are the same alias.
 * @param {string} name - A name, as typed or as stored.
 * @return {string} The normal form.
 */
export const normalizeAliasName = (name: string): string =>
  trimAliasName(name).toLowerCase().replace(ASCII_WHITESPACE_RUN, ' ');

// How a list of names reads as one text. The server joins with the same separator (AssetAlias::Lookup::SEPARATOR),
// which is why a name cannot hold a comma, and asset-alias-name-rules.json pins both. The catalog rows do carry
// the joined text (`aliasesText`), but the page swaps `aliases` for the remembered list right after an add or
// a remove (aliasesForRow), which leaves `aliasesText` stale: so the names are joined here, and the cell reads
// as the server's text would.
export const ALIAS_TEXT_SEPARATOR = ', ';

/**
 * The aliases as the one comma-separated text a table cell or an export shows.
 * @param {readonly string[]} names - The alias names, in the order they are shown.
 * @return {string} The names joined, blank with none.
 */
export const joinAliasNames = (names: readonly string[]): string =>
  names.join(ALIAS_TEXT_SEPARATOR);

export interface AliasNameCheck {
  /** Whether Add can be pressed. */
  canAdd: boolean;
  /** Why not, for the field. Null for a name that is fine and for an empty one, which isn't an error yet. */
  error: string | null;
}

/**
 * Checks a name the way the server will, before it is sent: not empty, no comma, within the length, and
 * not the same (case and spacing aside) as an alias already on the record.
 * @param {string} name - What the user typed.
 * @param {readonly { name: string }[]} existing - The aliases already on this record.
 * @param {string} recordLabel - What the aliases are on ("tract" or "development area"), for the duplicate message.
 * @return {AliasNameCheck} Whether the name can be added and the message to show if not.
 */
export const checkAliasName = (
  name: string,
  existing: readonly { name: string }[],
  recordLabel: string
): AliasNameCheck => {
  const trimmed = trimAliasName(name);
  if (trimmed === '') return { canAdd: false, error: null };
  if (trimmed.includes(',')) {
    return { canAdd: false, error: COMMA_ALIAS_MESSAGE };
  }
  // Characters, not UTF-16 units: the server counts characters.
  if (Array.from(trimmed).length > MAX_ALIAS_NAME_LENGTH) {
    return { canAdd: false, error: TOO_LONG_ALIAS_MESSAGE };
  }
  const normalized = normalizeAliasName(trimmed);
  if (existing.some((alias) => normalizeAliasName(alias.name) === normalized)) {
    return { canAdd: false, error: duplicateAliasMessage(recordLabel) };
  }
  return { canAdd: true, error: null };
};

/**
 * The message in a failed alias request, for the field. A 4xx carries the server's own: the name's
 * message (the alias endpoints send it as one string), else any other. A 404 (the record is gone; its
 * body is a bare "not found"), a 5xx, a network failure or a body with no message gets the fallback.
 * @param {unknown} error - What the request threw (ky throws an HTTPError for a non-2xx answer).
 * @param {string} fallback - What to say when the failure carried no message of its own.
 * @return {Promise<string>} A message to show.
 */
export async function aliasErrorMessage(
  error: unknown,
  fallback: string
): Promise<string> {
  if (
    !(error instanceof HTTPError) ||
    error.response.status >= 500 ||
    error.response.status === 404
  ) {
    return fallback;
  }
  try {
    const body = await error.response.json<{
      errors?: Record<string, string | string[]>;
    } | null>();
    const errors = body?.errors ?? {};
    const message = errors.name ?? Object.values(errors)[0];
    const text = Array.isArray(message) ? message.join(' ') : message;
    return text || fallback;
  } catch {
    // A body that isn't JSON has no message of its own.
    return fallback;
  }
}

// ─── Alias lists the user just changed, kept for the catalog they go back to ───
/*
 * Alias lists the user just changed on a record's page, kept for the catalog they go back to.
 *
 * The Tract Catalog's rows come from the search index. The request that adds or removes an alias writes the
 * tract's document before it answers (and falls back to a queued job if that write fails), but the index
 * shows a write only after its next refresh, about a second later, so a catalog opened right after the
 * change can still read the old list. The page remembers the list the server answered with and the catalog
 * shows it over the row until the index has certainly caught up. The page's own store goes away when the user leaves it, so this lives at module
 * scope. Nothing here is sent anywhere or persisted.
 */
export type AliasedKind = 'tract';

/**
 * How long a remembered list outranks the catalog's row. The index refresh takes about a second, and a
 * write that fell back to the queued job takes seconds; after this long the row is the truth again, so a
 * change someone else makes is not hidden.
 */
export const REMEMBERED_ALIASES_TTL_MS = 2 * 60 * 1_000;

interface Remembered {
  names: string[];
  at: number;
}

const remembered = new Map<string, Remembered>();

/**
 * The map key of one record's remembered list.
 * @param {AliasedKind} kind - Which catalog the record is listed in.
 * @param {string} recordKey - The row's key there.
 * @return {string} The key.
 */
const keyOf = (kind: AliasedKind, recordKey: string) => `${kind}:${recordKey}`;

/**
 * Notes a record's alias list after an add or a remove succeeded.
 * @param {AliasedKind} kind - Which catalog the record is listed in.
 * @param {string} recordKey - The row's key there (the tract's Salesforce id).
 * @param {readonly { name: string }[]} aliases - The full list the server answered with.
 * @param {number} [now] - The time, for tests.
 * @return {void}
 */
export function rememberAliases(
  kind: AliasedKind,
  recordKey: string,
  aliases: readonly { name: string }[],
  now: number = Date.now()
): void {
  remembered.set(keyOf(kind, recordKey), {
    names: aliases.map((alias) => alias.name),
    at: now,
  });
}

/**
 * The alias names a catalog row should show: the remembered list if the user changed this record a moment
 * ago, else what the row carries.
 * @param {AliasedKind} kind - Which catalog the row is in.
 * @param {string} recordKey - The row's key.
 * @param {string[]} fromRow - The aliases the fetched row carries.
 * @param {number} [now] - The time, for tests.
 * @return {string[]} The names to show.
 */
export function aliasesForRow(
  kind: AliasedKind,
  recordKey: string,
  fromRow: string[],
  now: number = Date.now()
): string[] {
  const entry = remembered.get(keyOf(kind, recordKey));
  if (!entry) return fromRow;
  if (now - entry.at > REMEMBERED_ALIASES_TTL_MS) {
    remembered.delete(keyOf(kind, recordKey));
    return fromRow;
  }
  return entry.names;
}

/** Forgets everything remembered. For tests. */
export function resetRememberedAliases(): void {
  remembered.clear();
}
